import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {Airport, AirportRouteRole} from '../model/airport';
import {matchesPassengerAge, passengerAgeCategory} from '../model/passenger-age';
import {ServiceEl, ServiceType, ServiceDirection} from '../model/serviceEl';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly STORAGE_KEY = 'airports';
  private readonly CART_KEY = 'cart';
  private readonly BOOKING_KEY = 'booking_data';
  private readonly AIRPORT_BOOKING_FORMS_KEY = 'airport_booking_forms';
  private readonly OWNER_KEY = 'booking_owner';

  private readonly MAX_AIRPORTS_COUNT = 20;

  private airportsSubject = new BehaviorSubject<Airport[]>([]);
  private cartSubject = new BehaviorSubject<ServiceEl[]>([]);
  public airports$ = this.airportsSubject.asObservable();
  public cart$ = this.cartSubject.asObservable();
  private serviceValidationSubmittedSubject = new BehaviorSubject<boolean>(false);
  public serviceValidationSubmitted$ = this.serviceValidationSubmittedSubject.asObservable();

  private bookingValidationSubmittedSubject = new BehaviorSubject<boolean>(false);
  public bookingValidationSubmitted$ = this.bookingValidationSubmittedSubject.asObservable();

  submitServiceValidation(): void {
    this.serviceValidationSubmittedSubject.next(true);
  }

  resetServiceValidation(): void {
    this.serviceValidationSubmittedSubject.next(false);
  }

  isServiceValidationSubmitted(): boolean {
    return this.serviceValidationSubmittedSubject.getValue();
  }

  submitBookingValidation(): void {
    this.bookingValidationSubmittedSubject.next(true);
  }

  resetBookingValidation(): void {
    this.bookingValidationSubmittedSubject.next(false);
  }

  isBookingValidationSubmitted(): boolean {
    return this.bookingValidationSubmittedSubject.getValue();
  }

  constructor() {
    this.loadAirports();
    this.loadCart();
  }

  saveBookingData(data: any): void {
    localStorage.setItem(this.BOOKING_KEY, JSON.stringify(data));
  }

  loadBookingData(): any {
    const stored = localStorage.getItem(this.BOOKING_KEY);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error('Error loading booking data:', error);
        return null;
      }
    }
    return null;
  }

  clearBookingData(): void {
    localStorage.removeItem(this.BOOKING_KEY);
  }

  private loadCart(): void {
    const stored = localStorage.getItem(this.CART_KEY);
    if (stored) {
      try {
        const cartData = JSON.parse(stored);
        const cartItems = cartData.map((data: any) => new ServiceEl(data));
        this.cartSubject.next(cartItems);
      } catch (error) {
        console.error('Error loading cart from storage:', error);
        this.cartSubject.next([]);
      }
    } else {
      this.cartSubject.next([]);
    }
  }

  private saveCart(cart: ServiceEl[]): void {
    localStorage.setItem(this.CART_KEY, JSON.stringify(cart));
    this.cartSubject.next([...cart]);
  }

  getCartItems(): Observable<ServiceEl[]> {
    return this.cart$;
  }

  getCartItemsSync(): ServiceEl[] {
    return this.cartSubject.getValue();
  }

  getCartItemByTitle(title: string): Observable<ServiceEl | null> {
    const cart = this.cartSubject.getValue();
    const item = cart.find((item) => item.title === title);
    return of(item || null);
  }

  private getCartKey(service: ServiceEl): string {
    return String(
      service.cardId ||
      `${service.airportId}_${service.serviceId ?? service.id}_${service.direction}`
    );
  }

  private isSameCartItem(a: ServiceEl, b: ServiceEl): boolean {
    const aKey = this.getCartKey(a);
    const bKey = this.getCartKey(b);

    if (!aKey || aKey.includes('undefined') || !bKey || bKey.includes('undefined')) {
      return false;
    }

    return aKey === bKey;
  }

  addToCart(service: ServiceEl): Observable<boolean> {
    const cart = this.cartSubject.getValue();

    const exists = cart.some((item) => this.isSameCartItem(item, service));

    if (exists) {
      return of(false);
    }

    if (!this.canAddServiceToAirport(service)) {
      return of(false);
    }

    const newItem = new ServiceEl({
      ...service,
      inCart: true,
    });

    const updatedCart = [...cart, newItem];

    this.saveCart(updatedCart);

    return of(true);
  }

  removeFromCart(title: string): Observable<boolean> {
    const cart = this.cartSubject.getValue();
    const initialLength = cart.length;
    const filteredCart = cart.filter((item) => item.title !== title);

    if (filteredCart.length !== initialLength) {
      this.saveCart(filteredCart);
      return of(true);
    }

    return of(false);
  }

  toggleCartItem(service: ServiceEl): Observable<boolean> {
    const cart = this.cartSubject.getValue();

    const exists = cart.some((item) => this.isSameCartItem(item, service));

    if (exists) {
      const filteredCart = cart.filter((item) => !this.isSameCartItem(item, service));

      this.saveCart(filteredCart);

      return of(true);
    }

    return this.addToCart(service);
  }

  updateCartItem(updatedService: ServiceEl): Observable<boolean> {
    const cart = this.cartSubject.getValue();

    const index = cart.findIndex((item) => this.isSameCartItem(item, updatedService));

    if (index !== -1) {
      cart[index] = new ServiceEl({
        ...updatedService,
        inCart: true,
      });

      this.saveCart(cart);
      return of(true);
    }

    return of(false);
  }

  isInCart(title: string): boolean {
    const cart = this.cartSubject.getValue();
    return cart.some((item) => item.title === title);
  }

  getCartCount(): Observable<number> {
    return new Observable<number>((observer) => {
      this.cart$.subscribe((cart) => {
        observer.next(cart.length);
      });
    });
  }

  getCartCountSync(): number {
    return this.cartSubject.getValue().length;
  }

  clearCart(): Observable<boolean> {
    this.saveCart([]);
    return of(true);
  }

  syncCartStatus(services: ServiceEl[]): ServiceEl[] {
    const cart = this.cartSubject.getValue();

    return services.map((service) => {
      const isInCart = cart.some((cartItem) => this.isSameCartItem(cartItem, service));

      return new ServiceEl({
        ...service,
        inCart: isInCart,
      });
    });
  }

  getCartStatistics(): Observable<Partial<Record<ServiceType, number>>> {
    return new Observable((observer) => {
      this.cart$.subscribe((cart) => {
        const stats: Partial<Record<ServiceType, number>> = {};

        cart.forEach((item) => {
          const type = item.types[0];

          if (!type) return;

          stats[type] = (stats[type] || 0) + 1;
        });

        observer.next(stats);
      });
    });
  }

  getCartStatisticsSync(): Partial<Record<ServiceType, number>> {
    const cart = this.cartSubject.getValue();
    const stats: Partial<Record<ServiceType, number>> = {};

    cart.forEach((item) => {
      const type = item.types[0];

      if (!type) return;

      stats[type] = (stats[type] || 0) + 1;
    });

    return stats;
  }

  getCartDisplayString(): Observable<string> {
    return new Observable((observer) => {
      this.getTotalServicesCount().subscribe((total) => {
        if (total === 0) {
          observer.next('');
        } else {
          observer.next(`Selected: ${total} service${total > 1 ? 's' : ''}`);
        }
      });
    });
  }

  getCartDisplayStringSync(): string {
    const total = this.getTotalServicesCountSync();

    if (total === 0) return '';

    return `Selected: ${total} service${total > 1 ? 's' : ''}`;
  }

  getTotalServicesCount(): Observable<number> {
    return new Observable((observer) => {
      this.cart$.subscribe((cart) => {
        observer.next(cart.length);
      });
    });
  }

  getTotalServicesCountSync(): number {
    return this.cartSubject.getValue().length;
  }

  private loadAirports(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        const airportsData = JSON.parse(stored);
        const airports = airportsData.map((data: any) => new Airport(data));
        const normalizedAirports = this.normalizeRouteAirports(airports);
        const sortedAirports = this.sortRouteAirports(normalizedAirports);

        this.airportsSubject.next(sortedAirports);
      } catch (error) {
        console.error('Error loading airports from storage:', error);
        this.airportsSubject.next([]);
      }
    } else {
      this.airportsSubject.next([]);
    }
  }

  private saveAirports(airports: Airport[]): void {
    const normalizedAirports = this.normalizeRouteAirports(airports);
    const sortedAirports = this.sortRouteAirports(normalizedAirports);

    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sortedAirports));
    this.airportsSubject.next([...sortedAirports]);
  }

  private updateActiveStatus(airports: Airport[], activeSlug: string): Airport[] {
    return airports.map((airport) => ({
      ...airport,
      active: airport.slug === activeSlug,
    }));
  }

  getAirports(): Observable<Airport[]> {
    return this.airports$;
  }

  getAirportsSync(): Airport[] {
    return this.airportsSubject.getValue();
  }

  getActiveAirport(): Observable<Airport | null> {
    const airports = this.airportsSubject.getValue();
    const activeAirport = airports.find((a) => a.active === true);
    return of(activeAirport || null);
  }

  getActiveAirportSync(): Airport | null {
    const airports = this.airportsSubject.getValue();
    return airports.find((a) => a.active === true) || null;
  }

  setActiveAirport(slug: string): Observable<boolean> {
    const airports = this.airportsSubject.getValue();
    const airportExists = airports.some((a) => a.slug === slug);

    if (airportExists) {
      const updatedAirports = this.updateActiveStatus(airports, slug);
      this.saveAirports(updatedAirports);
      return of(true);
    }

    return of(false);
  }

  getAirportBySlug(slug: string): Observable<Airport | null> {
    const airports = this.airportsSubject.getValue();
    const airport = airports.find((a) => a.slug === slug);
    return of(airport || null);
  }

  getAirportById(id: number): Observable<Airport | null> {
    const airports = this.airportsSubject.getValue();
    const airport = airports.find((a) => a.id === id);
    return of(airport || null);
  }

  removeAirportBySlug(slug: string): Observable<boolean> {
    const airports = this.airportsSubject.getValue();

    if (airports.length <= 1) {
      return of(false);
    }

    const initialLength = airports.length;
    const filteredAirports = airports.filter((a) => a.slug !== slug);

    if (filteredAirports.length !== initialLength) {
      let updatedAirports = filteredAirports;
      const wasActive = airports.find((a) => a.slug === slug)?.active;
      if (wasActive && updatedAirports.length > 0) {
        updatedAirports = this.updateActiveStatus(updatedAirports, updatedAirports[0].slug);
      }
      this.updateRouteAirportsOrder(updatedAirports);
      return of(true);
    }
    return of(false);
  }

  removeAirportByCityCode(cityCode: string): Observable<boolean> {
    const airports = this.airportsSubject.getValue();

    if (airports.length <= 1) {
      console.log('Нельзя удалить последний аэропорт');
      return of(false);
    }

    const normalizedCode = cityCode.toUpperCase();

    const filteredAirports = airports.filter(
      (airport) => airport.city_code?.toUpperCase() !== normalizedCode
    );

    if (filteredAirports.length === airports.length) {
      console.log('Аэропорт не найден:', cityCode);
      return of(false);
    }

    const updatedAirports = filteredAirports.map(
      (airport) =>
        new Airport({
          ...airport,
          active: false,
        })
    );

    this.saveAirports(updatedAirports);
    return of(true);
  }

  removeAirportById(id: number): Observable<boolean> {
    const airports = this.airportsSubject.getValue();
    const airportToRemove = airports.find((a) => a.id === id);

    if (!airportToRemove) {
      return of(false);
    }

    return this.removeAirportBySlug(airportToRemove.slug);
  }

  addAirport(airport: Airport): Observable<boolean> {
    const airports = this.airportsSubject.getValue();

    const existingAirport = airports.find((a) => a.slug === airport.slug);

    if (existingAirport) {
      this.resetServiceValidation();

      if (existingAirport.id) {
        this.removeCartItemsByAirportId(existingAirport.id);
      }

      const routeCode = this.getAirportRouteCode(existingAirport);
      this.removeAirportBookingForm(routeCode);

      const updatedAirports = airports.map(
        (a) =>
          new Airport({
            ...a,
            active: a.slug === existingAirport.slug,
          })
      );

      this.saveAirports(updatedAirports);

      return of(true);
    }

    if (airports.length >= this.MAX_AIRPORTS_COUNT) {
      console.log('Достигнут лимит аэропортов');
      return of(false);
    }

    this.resetServiceValidation();

    const updatedAirports = airports.map((a) => ({
      ...a,
      active: false,
    }));

    const newAirport = new Airport({
      ...airport,
      active: true,
      routeOrder: airports.length,
    });

    this.updateRouteAirportsOrder([...updatedAirports, newAirport]);

    return of(true);
  }

  updateAirport(airport: Airport): Observable<boolean> {
    const airports = this.airportsSubject.getValue();
    const index = airports.findIndex((a) => a.slug === airport.slug);

    if (index !== -1) {
      const updatedAirport = new Airport({
        ...airport,
        active: airport.active !== undefined ? airport.active : airports[index].active,
      });

      airports[index] = updatedAirport;
      this.saveAirports(airports);
      return of(true);
    }

    return of(false);
  }

  addOrUpdateAirport(airport: Airport): Observable<boolean> {
    const airports = this.airportsSubject.getValue();
    const index = airports.findIndex((a) => a.slug === airport.slug);

    if (index !== -1) {
      return this.updateAirport(airport);
    } else {
      return this.addAirport(airport);
    }
  }

  clearAllAirports(): Observable<boolean> {
    this.saveAirports([]);
    return of(true);
  }

  hasAirport(slug: string): boolean {
    const airports = this.airportsSubject.getValue();
    return airports.some((a) => a.slug === slug);
  }

  getAirportsCount(): number {
    return this.airportsSubject.getValue().length;
  }

  saveAirportBookingForm(cityCode: string, formData: any): void {
    const allForms = this.getAllAirportBookingForms();

    allForms[cityCode.toUpperCase()] = formData;

    localStorage.setItem(this.AIRPORT_BOOKING_FORMS_KEY, JSON.stringify(allForms));
  }

  getAirportBookingForm(cityCode: string): any | null {
    const allForms = this.getAllAirportBookingForms();

    return allForms[cityCode.toUpperCase()] || null;
  }

  private getAllAirportBookingForms(): Record<string, any> {
    const stored = localStorage.getItem(this.AIRPORT_BOOKING_FORMS_KEY);

    if (!stored) return {};

    try {
      return JSON.parse(stored);
    } catch {
      return {};
    }
  }

  saveOwnerData(data: any): void {
    localStorage.setItem(this.OWNER_KEY, JSON.stringify(data));
  }

  getOwnerData(): any | null {
    const stored = localStorage.getItem(this.OWNER_KEY);

    if (!stored) return null;

    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }

  getAirportRouteCode(airport: Airport): string {
    return airport.iata_code || airport.icao_code || airport.slug;
  }

  findAirportByRouteCode(code: string): Airport | undefined {
    const normalizedCode = code.toUpperCase();

    return this.getAirportsSync().find(
      (airport) =>
        airport.iata_code?.toUpperCase() === normalizedCode ||
        airport.icao_code?.toUpperCase() === normalizedCode ||
        airport.slug?.toUpperCase() === normalizedCode
    );
  }

  removeAirportByRouteCode(code: string): Observable<boolean> {
    this.resetServiceValidation();
    const airports = this.airportsSubject.getValue();

    if (airports.length <= 1) {
      console.log('Нельзя удалить последний аэропорт');
      return of(false);
    }

    const normalizedCode = code.toUpperCase();

    const airportToRemove = airports.find(
      (airport) =>
        airport.iata_code?.toUpperCase() === normalizedCode ||
        airport.icao_code?.toUpperCase() === normalizedCode ||
        airport.slug?.toUpperCase() === normalizedCode
    );

    if (!airportToRemove) {
      console.log('Аэропорт не найден:', code);
      return of(false);
    }

    if (airportToRemove.id) {
      this.removeCartItemsByAirportId(airportToRemove.id);
    }

    const routeCode = this.getAirportRouteCode(airportToRemove);
    this.removeAirportBookingForm(routeCode);

    const updatedAirports = airports.filter((airport) => airport.slug !== airportToRemove.slug);

    this.updateRouteAirportsOrder(updatedAirports);

    return of(true);
  }

  clearJourneyData(): void {
    for (const key of [
      this.STORAGE_KEY,
      this.CART_KEY,
      this.BOOKING_KEY,
      this.AIRPORT_BOOKING_FORMS_KEY,
      this.OWNER_KEY,
    ]) {
      localStorage.removeItem(key);
    }
    this.airportsSubject.next([]);
    this.cartSubject.next([]);
    this.resetServiceValidation();
    this.resetBookingValidation();
  }

  debugClearAll(): void {
    localStorage.clear();
    this.airportsSubject.next([]);
    this.cartSubject.next([]);
    console.log('STORAGE CLEARED');
  }

  getMaxAirportsCount(): number {
    return this.MAX_AIRPORTS_COUNT;
  }

  canAddAirport(): boolean {
    return this.getAirportsCount() < this.MAX_AIRPORTS_COUNT;
  }

  removeCartItemsByAirportId(airportId: number): void {
    const cart = this.cartSubject.getValue();

    const updatedCart = cart.filter((item) => Number(item.airportId) !== Number(airportId));

    this.saveCart(updatedCart);
  }

  removeAirportBookingForm(cityCode: string): void {
    const allForms = this.getAllAirportBookingForms();

    delete allForms[cityCode.toUpperCase()];

    localStorage.setItem(this.AIRPORT_BOOKING_FORMS_KEY, JSON.stringify(allForms));
  }

  removeSubmittedAirportByRouteCode(code: string): Observable<boolean> {
    const airports = this.airportsSubject.getValue();

    const normalizedCode = code.toUpperCase();

    const airportToRemove = airports.find(
      (airport) =>
        airport.iata_code?.toUpperCase() === normalizedCode ||
        airport.icao_code?.toUpperCase() === normalizedCode ||
        airport.slug?.toUpperCase() === normalizedCode
    );

    if (!airportToRemove) {
      console.log('Аэропорт не найден:', code);
      return of(false);
    }
    if (airportToRemove.id) {
      this.removeCartItemsByAirportId(airportToRemove.id);
    }

    const routeCode = this.getAirportRouteCode(airportToRemove);
    this.removeAirportBookingForm(routeCode);

    const updatedAirports = airports
      .filter((airport) => airport.slug !== airportToRemove.slug)
      .map((airport, index) => ({
        ...airport,
        active: index === 0,
      }));

    this.saveAirports(updatedAirports);

    return of(true);
  }

  getAirportServicesCount(airportId: number): number {
    return this.cartSubject
      .getValue()
      .filter((service) => Number(service.airportId) === Number(airportId)).length;
  }

  airportHasSelectedServices(airportId: number): boolean {
    return this.getAirportServicesCount(airportId) > 0;
  }

  getAirportsWithoutServices(): Airport[] {
    const airports = this.airportsSubject.getValue();

    return airports.filter((airport) => {
      if (!airport.id) return true;

      return !this.airportHasSelectedServices(airport.id);
    });
  }

  allAirportsHaveServices(): boolean {
    return this.getAirportsWithoutServices().length === 0;
  }

  isAirportBookingFormValid(routeCode: string): boolean {
    const form = this.getAirportBookingForm(routeCode);

    if (!form) return false;

    if (!form.flight?.flightNumber) return false;
    if (!form.flight?.date) return false;
    if (!form.flight?.time) return false;
    if (!form.flight?.departure) return false;

    if (!form.arrivalAirport && !form.flight?.arrival) return false;

    const ownerData = this.getOwnerData();

    const contact = ownerData?.contact || form.contact || {};

    const email = String(contact.email || '').trim();
    const phone = String(contact.phone || '').trim();
    const digits = phone.replace(/\D/g, '');

    const emailValid = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);

    const phoneValid = phone.startsWith('+') && digits.length >= 8 && digits.length <= 15;

    if (!emailValid || !phoneValid) return false;

    const passengers = form.passengers || [];

    if (passengers.length === 0) return false;

    const firstPassenger = ownerData?.firstPassenger || passengers[0];

    if (!firstPassenger?.name) return false;
    if (!firstPassenger?.surname) return false;
    const passport = String(firstPassenger.passportNumber || '').trim();

    if (!passport) return false;
    if (passport.length < 3 || passport.length > 20) return false;
    if (!firstPassenger.dateOfBirth) return false;

    for (let i = 1; i < passengers.length; i++) {
      const passenger = passengers[i];

      if (!passenger.name) return false;
      if (!passenger.surname) return false;
    }

    const counts = form.passengersState || {adult: passengers.length, children: 0};
    if (
      passengers.some(
        (passenger: any, index: number) =>
          !matchesPassengerAge(
            String((index === 0 ? firstPassenger : passenger).dateOfBirth || ''),
            String(form.flight.date),
            passengerAgeCategory(index, counts)
          )
      )
    )
      return false;
    return true;
  }

  getAirportsWithInvalidBookingForms(): Airport[] {
    return this.getAirportsWithSelectedServices().filter((airport) => {
      const routeCode = this.getAirportRouteCode(airport);

      return !this.isAirportBookingFormValid(routeCode);
    });
  }

  private getRouteRoleWeight(role?: AirportRouteRole): number {
    switch (role) {
      case 'departure':
        return 0;
      case 'transit':
        return 1;
      case 'arrival':
        return 2;
      default:
        return 3;
    }
  }

  private normalizeRouteAirports(airports: Airport[]): Airport[] {
    return airports.map(
      (airport, index) =>
        new Airport({
          ...airport,
          routeOrder: airport.routeOrder ?? index,
          routeRole: airport.routeRole,
        })
    );
  }

  private sortRouteAirports(airports: Airport[]): Airport[] {
    return [...airports].sort((a, b) => {
      const roleDiff = this.getRouteRoleWeight(a.routeRole) - this.getRouteRoleWeight(b.routeRole);

      if (roleDiff !== 0) return roleDiff;

      return (a.routeOrder ?? 0) - (b.routeOrder ?? 0);
    });
  }

  getDepartureAirport(): Airport | null {
    return this.getAirportsSync().find((airport) => airport.routeRole === 'departure') || null;
  }

  getTransitAirports(): Airport[] {
    return this.getAirportsSync().filter((airport) => airport.routeRole === 'transit');
  }

  getArrivalAirports(): Airport[] {
    return this.getAirportsSync().filter((airport) => airport.routeRole === 'arrival');
  }

  getRouteAirports(): Airport[] {
    return this.sortRouteAirports(this.getAirportsSync());
  }

  private canAddServiceToAirport(service: ServiceEl): boolean {
    if (!service.airportId || !service.direction) return true;

    if (!this.canAirportUseDirection(service.airportId, service.direction)) {
      return false;
    }

    const cart = this.cartSubject.getValue();

    const airportServices = cart.filter(
      (item) => Number(item.airportId) === Number(service.airportId)
    );

    if (airportServices.length === 0) return true;

    return airportServices.every((item) => item.direction === service.direction);
  }

  removeCartItemsByAirportIdExceptDirection(airportId: number, direction: ServiceDirection): void {
    const cart = this.cartSubject.getValue();

    const updatedCart = cart.filter((item) => {
      const sameAirport = Number(item.airportId) === Number(airportId);

      if (!sameAirport) return true;

      return item.direction === direction;
    });

    this.saveCart(updatedCart);
  }

  private setAirportRoleByService(service: ServiceEl): void {
    if (!service.airportId || !service.direction) return;

    const airports = this.airportsSubject.getValue();

    const updatedAirports = airports.map((airport) => {
      if (Number(airport.id) !== Number(service.airportId)) {
        return airport;
      }

      return new Airport({
        ...airport,
        routeRole: service.direction,
      });
    });

    this.updateRouteAirportsOrder(updatedAirports);
  }

  setAirportRouteRoleByAirportId(airportId: number, routeRole: AirportRouteRole): void {
    const airports = this.airportsSubject.getValue();

    const updatedAirports = airports.map((airport) => {
      if (Number(airport.id) !== Number(airportId)) {
        return airport;
      }

      return new Airport({
        ...airport,
        routeRole,
      });
    });

    this.saveAirports(updatedAirports);
  }

  hasDepartureAirport(): boolean {
    return this.getAirportsSync().some((airport) => airport.routeRole === 'departure');
  }

  hasArrivalAirport(): boolean {
    return this.getAirportsSync().some((airport) => airport.routeRole === 'arrival');
  }

  isRouteReadyForBooking(): boolean {
    return this.hasDepartureAirport() && this.hasArrivalAirport();
  }

  private syncAirportRoleAfterCartChange(airportId: number): void {
    const cart = this.cartSubject.getValue();

    const airportServices = cart.filter((item) => Number(item.airportId) === Number(airportId));

    const airports = this.airportsSubject.getValue();

    const updatedAirports = airports.map((airport) => {
      if (Number(airport.id) !== Number(airportId)) {
        return airport;
      }

      return new Airport({
        ...airport,
        routeRole: airportServices[0]?.direction,
      });
    });

    this.saveAirports(updatedAirports);
  }

  canAirportUseDirection(airportId: number, direction: ServiceDirection): boolean {
    const airport = this.getAirportsSync().find((item) => Number(item.id) === Number(airportId));

    if (!airport?.routeRole) return false;

    return airport.routeRole === direction;
  }

  updateTransitAirportsOrder(transitAirports: Airport[]): void {
    const currentAirports = this.getAirportsSync();

    const transitOrderMap = new Map<number, number>();

    transitAirports.forEach((airport, index) => {
      if (!airport.id) return;
      transitOrderMap.set(Number(airport.id), index);
    });

    const updatedAirports = currentAirports.map((airport) => {
      if (
        airport.routeRole === 'transit' &&
        airport.id &&
        transitOrderMap.has(Number(airport.id))
      ) {
        return new Airport({
          ...airport,
          routeOrder: transitOrderMap.get(Number(airport.id)) ?? airport.routeOrder,
        });
      }

      return airport;
    });

    this.saveAirports(updatedAirports);
  }

  serviceValidationSubmittedSubjectValue(): boolean {
    return this.serviceValidationSubmittedSubject.value;
  }

  bookingValidationSubmittedSubjectValue(): boolean {
    return this.bookingValidationSubmittedSubject.value;
  }

  updateRouteAirportsOrder(routeAirports: Airport[]): void {
    const lastIndex = routeAirports.length - 1;

    const updatedAirports = routeAirports.map((airport, index) => {
      let routeRole: AirportRouteRole;

      if (index === 0) {
        routeRole = 'departure';
      } else if (index === lastIndex) {
        routeRole = 'arrival';
      } else {
        routeRole = 'transit';
      }

      return new Airport({
        ...airport,
        routeOrder: index,
        routeRole,
      });
    });

    const airportRoles = new Map<number, AirportRouteRole>();

    updatedAirports.forEach((airport) => {
      if (!airport.id || !airport.routeRole) return;

      airportRoles.set(Number(airport.id), airport.routeRole);
    });

    const currentCart = this.cartSubject.getValue();

    const compatibleCart = currentCart.filter((service) => {
      if (!service.airportId || !service.direction) {
        return false;
      }

      const routeRole = airportRoles.get(Number(service.airportId));

      return routeRole === service.direction;
    });

    if (compatibleCart.length !== currentCart.length) {
      this.saveCart(compatibleCart);
    }

    this.saveAirports(updatedAirports);
  }

  hasSelectedServices(): boolean {
    return this.cartSubject.getValue().length > 0;
  }

  getAirportsWithSelectedServices(): Airport[] {
    const airportIds = new Set(
      this.cartSubject
        .getValue()
        .map((service) => Number(service.airportId))
        .filter((airportId) => Number.isFinite(airportId))
    );

    return this.getAirportsSync().filter(
      (airport) => airport.id && airportIds.has(Number(airport.id))
    );
  }
}
